/**
 * The booking form's phone field: a Canadian / US (NANP) number.
 *
 * It used to be a fixed "(###) ###-####" mask, which took the first ten
 * digits of whatever arrived: '+1 604-555-0112' (typed or autofilled) became
 * '(160) 455-5011', '+44 20 7946 0958' became '(442) 079-4609' and an
 * extension was cut off - all without an error, and the mangled number was
 * booked (QA 2026-09-25 #11). Now a leading +1 / 1 is read as the country
 * code, and anything that is not a 10-digit number is left as typed and
 * flagged, never silently rewritten.
 */

import i18n from '../i18n';

const digitsOf = (value: string): string => value.replace(/\D+/g, '');

/** True when the text names a country code other than +1. */
function isInternational(raw: string): boolean {
  const compact = raw.replace(/[\s().-]/g, '');
  return /^(\+|00)/.test(compact) && !/^(\+|00)1/.test(compact);
}

/** The national digits: without a +1 / 1 country code. */
export function nationalDigits(raw: string): string {
  const text = String(raw ?? '');
  const digits = digitsOf(text);
  if (isInternational(text)) return digits;
  const compact = text.replace(/[\s().-]/g, '');
  if (/^(\+|00)1/.test(compact)) {
    return digits.slice(compact.startsWith('00') ? 3 : 1);
  }
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
}

/**
 * The field's text as the visitor types: "(604) 555-0112" while it can be a
 * 10-digit number; exactly what they typed once it cannot (international,
 * an extension, too many digits), so the error below explains it.
 */
export function formatPhoneInput(raw: string): string {
  const text = String(raw ?? '');
  if (!text.trim()) return '';
  if (isInternational(text)) return text;
  const digits = nationalDigits(text);
  if (digits.length > 10 || /[a-z]/i.test(text)) return text;
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export interface PhoneCheck {
  valid: boolean;
  /** Shown under the field; undefined when valid or still empty. */
  error?: string;
}

/** Whether the booking can use this number, and if not, why. */
export function checkBookingPhone(raw?: string | null): PhoneCheck {
  const text = String(raw ?? '').trim();
  if (!text) return { valid: false };
  if (isInternational(text)) {
    return {
      valid: false,
      error: i18n.t('errors.phoneCountry'),
    };
  }
  const digits = nationalDigits(text);
  if (digits.length > 10 || /[a-z]/i.test(text)) {
    return {
      valid: false,
      error: i18n.t('errors.phoneExtension'),
    };
  }
  if (digits.length < 10 || !/^[2-9]/.test(digits)) {
    return {
      valid: false,
      error: i18n.t('errors.phoneDigits'),
    };
  }
  return { valid: true };
}

/**
 * The number as the form holds it when it is a bookable one, else nothing:
 * a half-typed or placeholder value is never sent anywhere (QA #22 saw
 * '(___) ___-____' in a booking draft).
 */
export function bookingPhoneOrUndefined(
  raw?: string | null,
): string | undefined {
  return checkBookingPhone(raw).valid ? String(raw).trim() : undefined;
}
